import React, { Fragment, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'rsuite';
import { isEmpty, has, set, split } from 'lodash/fp';
import { fetchPortalUsers } from '../../../../redux/Settings/sagas/portal/routines';
import {
  portalUsersOnClear,
  portalUsersOnChangePage,
  portalUsersOnChangeOrder
} from '../../../../redux/Settings/actions/portal/user';
import {
  getCurrentPageItems,
  getPaginationLoadedPages,
  getPaginationWithoutPages,
  getPaginationQueryOptions
} from '../../../../redux/Settings/selectors/portal/user/pagination';
import {
  getOrder,
  getIsFetching
} from '../../../../redux/Settings/selectors/portal/user';
import { getSettingsPortalUserSearchQuery } from '../../../../redux/Settings/selectors/components/portal/user-search';
import orderList from '../../../../helpers/orderList';
import Pagination from '../../../Pagination';
import DateCell from '../../../widgets/custom-cell/Date';
import CustomHeaderCell from '../../../widgets/custom-cell/HeaderCell';
import EmailCell from './EmailCell';
import NameCell from './NameCell';
import AddressCell from './AddressCell';
import PhoneCell from './PhoneCell';
import AutoHeightTable from '../../../AutoHeightTable';
import GuestNameCell from './GuestNameCell';

const { Column, Cell } = Table;

export default () => {
  const dispatch = useDispatch();
  const order = useSelector(getOrder);
  const paginationQuery = useSelector(getPaginationQueryOptions);
  const searchQuery = useSelector(getSettingsPortalUserSearchQuery);
  const isFetching = useSelector(getIsFetching);
  const items = useSelector(getCurrentPageItems);
  const pagination = useSelector(getPaginationWithoutPages);
  const loadedPages = useSelector(getPaginationLoadedPages);

  const query = { ...paginationQuery, ...searchQuery, order };
  const [sortColumn, sortType] = split(':', order);
  const orderedItems = useMemo(
    () => {
      if (isEmpty(items)) return [];
      return orderList(order, items);
    },
    [items, order]
  );

  useEffect(() => {
    dispatch(fetchPortalUsers());
  }, []);

  const handleSortColumn = (column, type) => {
    const newOrder = `${column}:${type}`;
    dispatch(portalUsersOnClear());
    dispatch(portalUsersOnChangeOrder(newOrder));
    dispatch(fetchPortalUsers({ ...query, order: newOrder }));
  };

  const handleChangePage = page => {
    const isPageLoaded = has(page, loadedPages);
    const nextPageQuery = set('page', page, query);
    if (!isPageLoaded) dispatch(fetchPortalUsers(nextPageQuery));
    else dispatch(portalUsersOnChangePage(page));
  };

  const handleChangeLimit = limit => {
    dispatch(portalUsersOnClear());
    dispatch(fetchPortalUsers({ ...query, limit, page: 1 }));
  };

  return (
    <Fragment>
      <AutoHeightTable
        bordered
        reduceHeight={540}
        wordWrap
        rowHeight={130}
        loading={isFetching}
        data={orderedItems}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={handleSortColumn}
      >
        <Column width={130} resizable>
          <CustomHeaderCell>ID</CustomHeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column width={150} sortable resizable>
          <CustomHeaderCell>Portal Name</CustomHeaderCell>
          <NameCell dataKey="fullName" />
        </Column>
        <Column width={150} resizable sortable>
          <CustomHeaderCell>Linked Guest</CustomHeaderCell>
          <GuestNameCell dataKey="guestName" />
        </Column>
        <Column width={300} sortable resizable>
          <CustomHeaderCell>Address</CustomHeaderCell>
          <AddressCell dataKey="fullAddress" />
        </Column>
        <Column width={250} resizable>
          <CustomHeaderCell>Contact Numbers</CustomHeaderCell>
          <PhoneCell dataKey="phone" />
        </Column>
        <Column width={220} sortable resizable>
          <CustomHeaderCell>Email</CustomHeaderCell>
          <EmailCell dataKey="email" />
        </Column>
        <Column width={110} sortable>
          <CustomHeaderCell>Date Verified</CustomHeaderCell>
          <DateCell dataKey="verifiedAt" emptyValue="" />
        </Column>
        <Column width={110} sortable>
          <CustomHeaderCell>Date Updated</CustomHeaderCell>
          <DateCell dataKey="updatedAt" emptyValue="" />
        </Column>
        <Column width={110} sortable>
          <CustomHeaderCell>Date Created</CustomHeaderCell>
          <DateCell dataKey="createdAt" />
        </Column>
      </AutoHeightTable>
      <Pagination
        page={pagination.currentPage}
        limit={pagination.limit}
        total={pagination.totalCount}
        onChangePage={handleChangePage}
        onChangeLimit={handleChangeLimit}
      />
    </Fragment>
  );
};
